// src/Header.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Header.css';

const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);

  const logoUrl = process.env.PUBLIC_URL + '/logo-two-line-color-3x.png';
  const supportEmail = process.env.REACT_APP_INFLEXXIONHEALTH_SUPPORT_EMAIL;

  const navRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileCloseRef = useRef(null);

  const toggleMenu = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  const closeMenus = useCallback(() => setOpenMenu(null), []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => {
      const next = !prev;
      if (!next) setMobileOpenSection(null);
      return next;
    });
  };

  const toggleMobileSection = (sectionName) => {
    setMobileOpenSection(mobileOpenSection === sectionName ? null : sectionName);
  };

  // Close desktop dropdown on Escape or outside click
  useEffect(() => {
    if (!openMenu) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenus();
    };
    const onClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) closeMenus();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [openMenu, closeMenus]);

  // Mobile menu: Escape closes, focus trap, focus return
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previouslyFocused = document.activeElement;
    // Move focus into the menu
    if (mobileCloseRef.current) mobileCloseRef.current.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setMobileOpenSection(null);
        return;
      }
      if (e.key === 'Tab' && mobileMenuRef.current) {
        const focusables = mobileMenuRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [mobileMenuOpen]);

  const renderDesktopDropdown = (key, label, items) => {
    const isOpen = openMenu === key;
    const submenuId = `submenu-${key}`;
    return (
      <li className="menu__item">
        <button
          type="button"
          className="menu__link menu__link--button"
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-controls={submenuId}
          onClick={() => toggleMenu(key)}
        >
          {label}
          <span className="menu__chevron" aria-hidden="true">▾</span>
        </button>
        {isOpen && (
          <ul className="menu__submenu" id={submenuId}>
            {items.map(({ href, text }) => (
              <li key={href}>
                <a href={href} onClick={closeMenus}>{text}</a>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const renderMobileSection = (key, label, items) => {
    const isOpen = mobileOpenSection === key;
    const listId = `mobile-list-${key}`;
    return (
      <div className="mobile-menu__section">
        <button
          type="button"
          className="mobile-menu__section-toggle"
          aria-expanded={isOpen}
          aria-controls={listId}
          onClick={() => toggleMobileSection(key)}
        >
          <span className="mobile-menu__title">{label}</span>
          <span className="mobile-menu__toggle-icon" aria-hidden="true"></span>
        </button>
        {isOpen && (
          <ul className="mobile-menu__list" id={listId}>
            {items.map(({ href, text }) => (
              <li key={href}>
                <a href={href}>{text}</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // Mirrors the nav on inflexxionhealth.com (state guides flattened into
  // Resources; our dropdowns are one level deep).
  const careItems = [
    { href: 'https://inflexxionhealth.com/symptoms-we-treat', text: 'Symptoms We Treat' },
    { href: 'https://inflexxionhealth.com/our-care-team', text: 'Our Care Team' },
    { href: 'https://inflexxionhealth.com/consultation-services', text: 'Our Consultation Services' },
    { href: 'https://inflexxionhealth.com/our-custom-rx', text: 'Our Custom Rx' },
  ];
  const resourcesItems = [
    { href: 'https://inflexxionhealth.com/blog', text: 'Blog' },
    { href: 'https://inflexxionhealth.com/faqs', text: 'FAQs' },
    { href: 'https://inflexxionhealth.com/sexual-health-guide', text: 'Sexual Health Guide' },
    { href: 'https://inflexxionhealth.com/testosterone-for-women-guide', text: 'Testosterone Guide' },
    { href: 'https://inflexxionhealth.com/menopause-treatment-gap-report-1-in-5-women-receive-hrt', text: 'Menopause Care Survey Report' },
    { href: 'https://inflexxionhealth.com/arizona-menopause-guide', text: 'Arizona Menopause Resources' },
    { href: 'https://inflexxionhealth.com/colorado-menopause-guide', text: 'Colorado Menopause Resources' },
    { href: 'https://inflexxionhealth.com/utah-menopause-guide', text: 'Utah Menopause Resources' },
  ];
  const aboutItems = [
    { href: 'https://inflexxionhealth.com/our-vision', text: 'Our Vision' },
    { href: 'https://inflexxionhealth.com/inflexxion-health-founders-story', text: "Founder's Story" },
    { href: 'https://inflexxionhealth.com/partner-with-us', text: 'Partner With Us' },
  ];

  return (
    <header className="header">
      <div className="header__container">
        <div className="header__logo">
          <a href="https://inflexxionhealth.com" aria-label="Inflexxion Health home">
            <img
              src={logoUrl}
              alt="Inflexxion Health"
              className="logo"
              style={{ width: '180px' }}
            />
          </a>
        </div>

        <nav className="header__nav header__nav--desktop" aria-label="Primary" ref={navRef}>
          <ul className="menu">
            {renderDesktopDropdown('care', 'Our Care', careItems)}
            {renderDesktopDropdown('resources', 'Resources', resourcesItems)}
            {renderDesktopDropdown('about', 'About', aboutItems)}
            <li className="menu__item">
              <a href="https://inflexxionhealth.com/book-your-visit-new#step-1" className="menu__cta">Book Your Visit</a>
            </li>
            <li className="menu__item">
              <a href="https://inflexxionhealth.gethealthie.com/" className="menu__link">Login</a>
            </li>
            <li className="menu__item">
              {/* The site's HubSpot web-interactive CTA; linked straight to its
                  destination rather than through the tracking redirect. */}
              <a href="https://inflexxionhealth.com/stages-of-menopause" className="menu__cta last">
                Is It Menopause? Find Out
              </a>
            </li>
          </ul>
        </nav>

        <button
          type="button"
          className="header__mobile-toggle"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          onClick={toggleMobileMenu}
          ref={hamburgerRef}
        >
          <span className="hamburger-icon" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      <div
        id="mobile-menu"
        className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!mobileMenuOpen}
        ref={mobileMenuRef}
      >
        <button
          type="button"
          className="mobile-menu__close"
          onClick={toggleMobileMenu}
          ref={mobileCloseRef}
        >
          <span><strong>Close</strong></span>
          <span className="mobile-menu__close-icon" aria-hidden="true">✕</span>
        </button>

        <nav className="mobile-menu__nav" aria-label="Mobile primary">
          {renderMobileSection('care', 'Our Care', careItems)}
          {renderMobileSection('resources', 'Resources', resourcesItems)}
          {renderMobileSection('about', 'About', aboutItems)}

          <div className="mobile-menu__cta">
            <a href="https://inflexxionhealth.com/book-your-visit-new#step-1" className="mobile-menu__login">
              Book Your Visit
            </a>
            <a href="https://inflexxionhealth.com/stages-of-menopause" className="mobile-menu__login">
              Is It Menopause? Find Out
            </a>
            <a
              href="https://inflexxionhealth.gethealthie.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-menu__login"
            >
              Login
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
            {supportEmail && (
              <div className="mobile-menu__email">
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
